import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { HEADLINE_SLOTS, DEFAULT_HEADLINES } from '../lib/heroHeadlines';
import { DEFAULT_FREE_ENTRY } from '../lib/siteCopy';
import { RotateCcw, Save } from 'lucide-react';
import FaqManager from './FaqManager';

const label = (slot) => slot.replace('-', ' · ');

export default function AdminCopy() {
  const [lines, setLines] = useState(null); // {slot: [l1, l2]}
  const [freeEntry, setFreeEntry] = useState({ ...DEFAULT_FREE_ENTRY });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get('/site-settings/hero_headlines').then((r) => {
      const overrides = r.data?.value?.lines || {};
      const merged = {};
      HEADLINE_SLOTS.forEach((s) => {
        const o = Array.isArray(overrides[s]) ? overrides[s] : [];
        merged[s] = [o[0] ?? DEFAULT_HEADLINES[s][0], o[1] ?? DEFAULT_HEADLINES[s][1]];
      });
      setLines(merged);
    });
    api.get('/site-settings/free_entry_note').then((r) => {
      const v = r.data?.value || {};
      setFreeEntry({
        title: (v.title || '').trim() || DEFAULT_FREE_ENTRY.title,
        body: (v.body || '').trim() || DEFAULT_FREE_ENTRY.body,
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Store only slots that differ from the defaults — everything else keeps
      // tracking the built-in copy.
      const overrides = {};
      HEADLINE_SLOTS.forEach((s) => {
        const cur = (lines[s] || []).map((x) => (x || '').trim());
        const def = DEFAULT_HEADLINES[s];
        if (cur[0] !== def[0] || cur[1] !== def[1]) overrides[s] = cur;
      });
      await api.put('/admin/site-settings/hero_headlines', { value: { lines: overrides } });
      const fe = {};
      if (freeEntry.title.trim() !== DEFAULT_FREE_ENTRY.title) fe.title = freeEntry.title.trim();
      if (freeEntry.body.trim() !== DEFAULT_FREE_ENTRY.body) fe.body = freeEntry.body.trim();
      await api.put('/admin/site-settings/free_entry_note', { value: fe });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const resetSlot = (s) => setLines({ ...lines, [s]: [...DEFAULT_HEADLINES[s]] });

  if (!lines) return <div className="p-8 text-muted text-sm">Loading…</div>;

  return (
    <div data-testid="admin-copy-page">
      <h1 className="font-display text-3xl mb-2">Homepage copy</h1>
      <p className="text-sm text-charcoal/60 mb-6 max-w-2xl">
        The homepage headline rotates by season and time of day — two variants per slot.
        Edit any line here and it changes on the <strong>live site instantly</strong>, no republish needed.
        &ldquo;Reset&rdquo; puts a slot back to the built-in wording.
      </p>
      <div className="sticky top-0 z-10 bg-admin py-3 mb-4 flex items-center gap-3">
        <button onClick={save} disabled={saving} data-testid="copy-save-btn"
          className="inline-flex items-center gap-2 bg-heritage text-cream font-semibold rounded-full px-6 py-2.5 text-sm hover:bg-heritage/90 disabled:opacity-50">
          <Save size={14}/> {saving ? 'Saving…' : 'Save changes'}
        </button>
        {saved && <span className="text-forest text-sm font-semibold" data-testid="copy-saved-note">Saved — live immediately ✓</span>}
      </div>
      <div className="space-y-4">
        <div className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid="copy-free-entry">
          <div className="flex items-center justify-between mb-3">
            <div className="label text-heritage">Trade Partners · free entry wording</div>
            <div className="flex items-center gap-2">
              {(freeEntry.title !== DEFAULT_FREE_ENTRY.title || freeEntry.body !== DEFAULT_FREE_ENTRY.body) &&
                <span className="text-[10px] uppercase tracking-wider font-bold bg-mustard/30 text-charcoal px-2 py-0.5 rounded-full">Edited</span>}
              <button onClick={() => setFreeEntry({ ...DEFAULT_FREE_ENTRY })} title="Reset to built-in wording" data-testid="copy-free-entry-reset"
                className="inline-flex items-center gap-1 text-xs text-charcoal/50 hover:text-heritage">
                <RotateCcw size={11}/> Reset
              </button>
            </div>
          </div>
          <input value={freeEntry.title}
            onChange={(e) => setFreeEntry({ ...freeEntry, title: e.target.value })}
            data-testid="copy-free-entry-title"
            className="w-full bg-base border border-charcoal/10 rounded-xl px-4 py-2.5 text-sm mb-2 focus:outline-none focus:border-heritage" />
          <textarea value={freeEntry.body} rows={4}
            onChange={(e) => setFreeEntry({ ...freeEntry, body: e.target.value })}
            data-testid="copy-free-entry-body"
            className="w-full bg-base border border-charcoal/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-heritage" />
          <div className="text-xs text-charcoal/45 mt-1">Shown on the Trade Partners page beneath the membership packages.</div>
        </div>
        {HEADLINE_SLOTS.map((s) => {
          const isDefault = lines[s][0] === DEFAULT_HEADLINES[s][0] && lines[s][1] === DEFAULT_HEADLINES[s][1];
          return (
            <div key={s} className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid={`copy-slot-${s}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="label text-heritage capitalize">{label(s)}</div>
                <div className="flex items-center gap-2">
                  {!isDefault && <span className="text-[10px] uppercase tracking-wider font-bold bg-mustard/30 text-charcoal px-2 py-0.5 rounded-full">Edited</span>}
                  <button onClick={() => resetSlot(s)} title="Reset to built-in wording" data-testid={`copy-reset-${s}`}
                    className="inline-flex items-center gap-1 text-xs text-charcoal/50 hover:text-heritage">
                    <RotateCcw size={11}/> Reset
                  </button>
                </div>
              </div>
              {[0, 1].map((i) => (
                <input key={i} value={lines[s][i]}
                  onChange={(e) => { const next = [...lines[s]]; next[i] = e.target.value; setLines({ ...lines, [s]: next }); }}
                  data-testid={`copy-input-${s}-${i}`}
                  className="w-full bg-base border border-charcoal/10 rounded-xl px-4 py-2.5 text-sm mb-2 focus:outline-none focus:border-heritage" />
              ))}
            </div>
          );
        })}
        <FaqManager />
      </div>
    </div>
  );
}
